import createClient, { type Middleware, type MiddlewareCallbackParams } from 'openapi-fetch';
import type { paths } from '~~/types/Api';
import { getApiErrorMessage } from '~~/models/ApiResponse';
import { ApiError } from '~~/types/ApiError';
import { toNetworkError } from '~~/types/NetworkError';
import { useSupabaseClient } from '~/composables/useSupabaseClient';
import { reportSlowRequest } from '~/utils/reportSlowRequest';
import { useAppToast } from '~/composables/useAppToast';

/**
 * We're using "openapi-fetch" instead of Nuxt's built-in "ofetch" because "ofetch"
 * doesn't fully comply with the official fetch specification
 * (see: https://github.com/openapi-ts/openapi-typescript/issues/1691#issuecomment-2183578979).
 * Additionally, "ofetch" is incompatible with "openapi-typescript" types
 * (see: https://github.com/unjs/ofetch/issues/406).
 *
 * If either of these issues are resolved in the future, we should refactor this
 * to ensure consistent fetch functions across the application.
 */

export default defineNuxtPlugin({
	name: 'api-client',
	setup: () => {
		const supabaseClient = useSupabaseClient();

		/**
         * We want to explicity throw an error to ensure Tanstack Query works
         * https://openapi-ts.dev/openapi-fetch/middleware-auth#throwing
         */
		const throwOnError: Middleware = {
			async onResponse({ response, request, schemaPath }) {
				if (response.status >= 400) {
					const body = response.headers.get('content-type')?.includes('json')
						? await response.clone().json()
						: await response.clone().text();

					const errorMessage = getApiErrorMessage(body)
						|| `Request failed with status ${response.status} (${response.statusText})`;

					const error = new ApiError({
						message: errorMessage,
						status: response.status,
						url: response.url,
						schemaPath,
						method: request.method,
						data: body,
					});

					throw error;
				}
				return undefined;
			},
		};

		const toast = useAppToast();
		const router = useRouter();
		const nuxtApp = useNuxtApp();
		const UNAUTHORIZED_TOAST_ID = 'unauthorized';

		const authMiddleware: Middleware = {
			async onResponse({ response }) {
				if (response.status === 401) {
					if (!toast.toasts.value.some((to) => to.id === UNAUTHORIZED_TOAST_ID)) {
						toast.error({
							id: UNAUTHORIZED_TOAST_ID,
							title: nuxtApp.$i18n.t('toast.unauthorized.title'),
							description: nuxtApp.$i18n.t('toast.unauthorized.description'),
						});
					}
					const result = await supabaseClient.auth.signOut({ scope: 'local' });

					if (result.error) {
						// @ts-expect-error: signOut is not working when the user has an invalid session. It's throwing an 403 and is not deleting the cookies or local storage. Therefore we try to do this manually. Unfortunately "storageKey" is a private member of client. (https://github.com/supabase/auth-js/pull/894)
						localStorage.removeItem(supabaseClient.storageKey);
					}

					const merchantSlug = 'merchantSlug' in router.currentRoute.value.params ? router.currentRoute.value.params.merchantSlug : null;

					await navigateTo(merchantSlug
						? { name: 'MerchantLogin', params: { merchantSlug } }
						: { name: 'GlobalLogin' },
					);
				}
			},
			async onRequest({ request, schemaPath }) {
				const { data, error } = await supabaseClient.auth.getSession();

				if (error) {
					throw toNetworkError(request, error, schemaPath);
				}

				const accessToken = data.session?.access_token;

				if (!accessToken) {
					return;
				}

				request.headers.set('Authorization', `Bearer ${accessToken}`);

				return request;
			},
		};

		const { public: { apiBaseUrl, maintenance: isMaintenanceModeEnabled } } = useRuntimeConfig();

		const disableOnMaintenance: Middleware = {
			onRequest() {
				if (isMaintenanceModeEnabled) {
					throw new Error('App is currently under maintenance');
				}
			},
		};

		const enrichNetworkError: Middleware = {
			onError: ({ request, error, schemaPath }) => toNetworkError(request, error, schemaPath),
		};

		type RequestTiming = {
			startedAt: number;
			startedHidden: boolean;
			sentAt?: number;
		};

		const requestTimings = new Map<string, RequestTiming>();
		let lastHiddenAt = -Infinity;

		document.addEventListener('visibilitychange', () => {
			if (document.hidden) {
				lastHiddenAt = performance.now();
			}
		});

		const startTimer: Middleware = {
			onRequest({ id }) {
				requestTimings.set(id, { startedAt: performance.now(), startedHidden: document.hidden });
			},
		};

		const finishTiming = ({ id, request, schemaPath }: MiddlewareCallbackParams, status?: number) => {
			const timing = requestTimings.get(id);
			requestTimings.delete(id);

			if (!timing) {
				return;
			}

			const finishedAt = performance.now();

			reportSlowRequest({
				method: request.method,
				schemaPath,
				status,
				durationMs: finishedAt - timing.startedAt,
				authMs: (timing.sentAt ?? finishedAt) - timing.startedAt,
				pageHidden: timing.startedHidden || document.hidden || lastHiddenAt > timing.startedAt,
			});
		};

		const stopTimer: Middleware = {
			onRequest({ id }) {
				const timing = requestTimings.get(id);

				if (timing) {
					timing.sentAt = performance.now();
				}
			},
			onResponse: (params) => finishTiming(params, params.response.status),
			onError: (params) => finishTiming(params),
		};

		const api = createClient<paths>({
			baseUrl: apiBaseUrl,
		});

		/**
         * The order is important as "throwOnError" will not triger
         * "authMiddleware" when throwing an error.
         * "stopTimer" goes last, as "onResponse" and "onError" run in reverse order.
         * Its "onRequest" then runs after "authMiddleware", which marks the end of the session wait.
         * A throwing "onRequest" skips all hooks, hence "startTimer" after "disableOnMaintenance".
         */
		api.use(disableOnMaintenance);
		api.use(startTimer);
		api.use(enrichNetworkError);
		api.use(throwOnError);
		api.use(authMiddleware);
		api.use(stopTimer);

		// Expose to useNuxtApp().$api
		return {
			provide: {
				api,
			},
		};
	},
});
